import React from 'react';
import {
  Box,
  Typography,
  Switch,
  useTheme
} from '@mui/material';
import { TemplateCategory } from '@/types/templateCategoryTypes'; // Adjust path
import TemplateCategoryPicker from '@/components/TemplateCategoryPicker/TemplateCategoryPicker'; // Adjust path
import { UiTemplate } from '@/pages/TemplateEditPage/templateEditTypes';
import { AttioTextField } from '@/components/AttioForm';

interface TemplateSettingsFormProps {
  template: UiTemplate;
  templateCategories: TemplateCategory[];
  isLoadingCategories: boolean;
  isSaving: boolean;
  onUpdateDetailsLocal: (updates: Partial<UiTemplate>) => void;
  onSaveSettings: (immediateUpdates?: Partial<UiTemplate>) => void; // For blur/immediate switch saves
}

const TemplateSettingsForm: React.FC<TemplateSettingsFormProps> = ({
  template,
  templateCategories,
  isLoadingCategories,
  isSaving,
  onUpdateDetailsLocal,
  onSaveSettings
}) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}>
      <AttioTextField
        label="Template Name"
        value={template.name}
        onChange={(e) => onUpdateDetailsLocal({ name: e.target.value })}
        disabled={isSaving}
        required
        placeholder="e.g., Interview Template"
      />

      <AttioTextField
        label="Description"
        value={template.description}
        onChange={(e) =>
          onUpdateDetailsLocal({ description: e.target.value })
        }
        multiline
        minRows={2}
        maxRows={6}
        disabled={isSaving}
        placeholder="Describe the purpose of this template"
      />

      <AttioTextField
        label="AI Title Generation Prompt"
        value={template.titleGenerationPrompt || ''}
        onChange={(e) =>
          onUpdateDetailsLocal({ titleGenerationPrompt: e.target.value })
        }
        multiline
        minRows={3}
        maxRows={10}
        disabled={isSaving}
        placeholder="Enter a custom prompt for AI to generate meaningful titles for intake sessions using this template. Leave blank to use the default prompt."
        description="This prompt guides the AI in generating descriptive titles for intake sessions based on transcript content."
      />

      <Box
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: theme.palette.background.paper,
            padding: 0,
            '& fieldset': {
              borderColor: theme.palette.divider
            },
            '&:hover fieldset': {
              borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
              borderWidth: '1px'
            }
          },
          '& .MuiInputBase-input': {
            padding: '8px 12px',
            fontSize: '14px',
            fontWeight: 400,
            textAlign: 'left'
          },
          '& .MuiInputLabel-root': {
            display: 'none'
          }
        }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            mb: 0.75,
            display: 'block'
          }}
        >
          Template Category *
        </Typography>
        <TemplateCategoryPicker
          value={template.templateCategoryId || ''}
          onChange={(e) => {
            const newCategoryId =
              e.target.value === '' ? null : Number(e.target.value);
            const selectedCategory = templateCategories.find(
              (tc) => tc.id === newCategoryId
            );
            onUpdateDetailsLocal({
              templateCategoryId: newCategoryId as number,
              category: selectedCategory
            });
          }}
          categories={templateCategories}
          isLoadingCategories={isLoadingCategories}
          disabled={isSaving || isLoadingCategories}
          required={true}
          fullWidth
          variant="outlined"
          labelId="template-edit-category-label"
          id="template-edit-category-select"
          label=""
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px',
          px: 2,
          py: 1.5,
          backgroundColor: theme.palette.background.paper
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.text.primary
          }}
        >
          Archived
        </Typography>
        <Switch
          checked={template.isArchived}
          onChange={(e) => {
            const newArchivedState = e.target.checked;
            onUpdateDetailsLocal({ isArchived: newArchivedState });
          }}
          disabled={isSaving}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '8px',
          px: 2,
          py: 1.5,
          backgroundColor: theme.palette.background.paper
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.text.primary
          }}
        >
          Requires Approval
        </Typography>
        <Switch
          checked={template.requiresApproval || false}
          onChange={(e) => {
            const newApprovalState = e.target.checked;
            onUpdateDetailsLocal({
              requiresApproval: newApprovalState
            });
          }}
          disabled={isSaving}
        />
      </Box>
    </Box>
  );
};

export default TemplateSettingsForm;
